<script lang="ts" setup>
import type { RadiobuttonGroupOption } from "@/shared/@types/RadiobuttonGroupOption";
import { computed, toRefs } from "vue";
import LabelInfoTooltip from "../LabelInfoTooltip/LabelInfoTooltip.vue";

const props = withDefaults(
  defineProps<{
    modelValue: string | undefined;
    options: Array<RadiobuttonGroupOption>;
    errors?: Array<string>;
    disabled?: boolean;
    alignment?: "horizontal" | "vertical";
  }>(),
  { errors: () => [] },
);

const { modelValue } = toRefs(props);

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const currentModelValue = computed({
  get: () => modelValue.value || "",
  set: (value) => {
    emit("update:modelValue", value);
  },
});
</script>

<template>
  <div
    class="radiobutton-group"
    role="radiogroup"
    :data-alignment="alignment"
    :data-disabled="disabled"
  >
    <div class="radiobutton-group--wrapper">
      <label
        v-for="option in options"
        :key="option.key"
        class="radiobutton-group--option"
        :data-checked="option.key === currentModelValue"
      >
        <div class="radiobutton-group--input-wrapper">
          <input
            v-model="currentModelValue"
            class="radiobutton-group--input"
            type="radio"
            :value="option.key"
            :disabled="disabled"
          />
        </div>
        <div class="radiobutton-group--label">
          <i18n-t
            v-if="option.captionWithLink"
            :keypath="option.captionWithLink.key"
            tag="span"
          >
            <template #link>
              <a
                :href="option.captionWithLink.url"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ option.captionWithLink.text }}
              </a>
            </template>
          </i18n-t>
          <span v-else>{{ option.caption }}</span>
          <LabelInfoTooltip
            v-if="option.info"
            :title="option.info?.title"
            :text="option.info.text"
          />
        </div>
      </label>
    </div>
    <span
      v-if="errors.length > 0"
      class="radiobutton-group--error-message"
    >
      {{ errors[0] }}
    </span>
  </div>
</template>

<style scoped>
@import "RadiobuttonGroup.css";
</style>
